
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  Box,
  Typography,
  RadioGroup,
  FormControl,
  FormLabel,
  FormGroup
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { ICustomFormField } from '../../../../../modules/masterdata/types';
import ResponsiveSelect from '../../../../../components/ResponsiveSelect';

interface CustomFormFieldPreviewModalProps {
  open: boolean;
  onClose: () => void;
  field: ICustomFormField | null;
}

const CustomFormFieldPreviewModal: React.FC<CustomFormFieldPreviewModalProps> = ({
  open,
  onClose,
  field
}) => {
  if (!field) return null;

  const { field_name, field_type, is_required, field_options } = field;
  const displayLabel = field_name || 'Field Preview';
  const options = field_options || [];

  // Transform options for ResponsiveSelect if needed (array of strings to objects)
  const selectOptions = options.map(opt => ({ value: opt, label: opt }));

  const renderField = () => {
    switch (field_type) {
      case 'text':
        return (
          <TextField
            fullWidth
            label={displayLabel}
            required={is_required}
            variant="outlined"
            placeholder="Text input preview"
            size="small"
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            label={displayLabel}
            required={is_required}
            type="number"
            variant="outlined"
            placeholder="Number input preview"
            size="small"
          />
        );
      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker format="DD/MM/YYYY"
              label={displayLabel}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: is_required,
                  size: 'small'
                }
              }}
            />
          </LocalizationProvider>
        );
      case 'dropdown':
        // We can't use hooks inside this render function if we want to be clean, 
        // but for a simple preview, a controlled component needs state.
        // However, we just want to show the UI. Uncontrolled or dummy state is fine.
        return (
          <ResponsiveSelect
            label={displayLabel}
            value=""
            onChange={() => { }}
            options={selectOptions}
            required={is_required}
          />
        );
      case 'checkbox':
        if (options && options.length > 0) {
          return (
            <FormControl component="fieldset">
              <FormLabel component="legend">{displayLabel}{is_required ? ' *' : ''}</FormLabel>
              <FormGroup>
                {options.map((opt, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={<Checkbox size="small" />}
                    label={opt}
                  />
                ))}
              </FormGroup>
            </FormControl>
          )
        }
        return (
          <FormControlLabel
            control={<Checkbox size="small" />}
            label={<Typography variant="body2">{displayLabel}{is_required ? ' *' : ''}</Typography>}
          />
        );
      case 'radio':
        if (options && options.length > 0) {
          return (
            <FormControl component="fieldset">
              <FormLabel component="legend">{displayLabel}{is_required ? ' *' : ''}</FormLabel>
              <RadioGroup>
                {options.map((opt, idx) => (
                  <FormControlLabel
                    key={idx}
                    value={opt}
                    control={<Radio size="small" />}
                    label={opt}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )
        }
        return (
          <FormControlLabel
            control={<Radio size="small" />}
            label={<Typography variant="body2">{displayLabel}{is_required ? ' *' : ''}</Typography>}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            label={displayLabel}
            required={is_required}
            disabled
            value={`Unknown Field Type: ${field_type}`}
            size="small"
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Preview Field</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mt: 2,
            p: 3,
            border: '1px dashed #bdbdbd',
            borderRadius: 2,
            bgcolor: '#fafafa',
            position: 'relative',
            minHeight: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box width="100%">
            {renderField()}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CustomFormFieldPreviewModal;
